import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import DealerNav from "./DealerNav";

const API = process.env.REACT_APP_API_BASE; // e.g. https://api.meshum.com

export default function DealerInventoryEdit() {
  const { id } = useParams();
  const navigate = useNavigate();

  const token = useMemo(() => localStorage.getItem("dealerToken") || "", []);
  const headers = useMemo(
    () => ({ Authorization: `Bearer ${token}`, "Content-Type": "application/json" }),
    [token]
  );

  // Redirect if not logged in
  useEffect(() => {
    if (!token) navigate("/dealer/login", { replace: true });
  }, [token, navigate]);

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);

  // Car state
  const [car, setCar] = useState(null);
  const [price, setPrice] = useState("");
  const [mileage, setMileage] = useState("");
  const [trim, setTrim] = useState("");
  const [sold, setSold] = useState(false);

  // Load car
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setErr("");
      try {
        const res = await fetch(`${API}/dealer/inventory/${id}`, { headers });
        if (res.status === 401) {
          navigate("/dealer/login", { replace: true });
          return;
        }
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        setCar(data);
        setPrice(data.price ?? "");
        setMileage(data.mileage ?? "");
        setTrim(data.trim ?? "");
        setSold(!!data.soldAt);
      } catch (e) {
        setErr("Could not load vehicle.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [API, id, headers, navigate]);

  const save = async () => {
    setSaving(true);
    setErr("");
    try {
      const body = {
        price: price === "" ? null : Number(price),
        mileage: mileage === "" ? null : Number(mileage),
        trim: trim ?? "",
        sold
      };
      const res = await fetch(`${API}/dealer/inventory/${id}`, {
        method: "PATCH",
        headers,
        body: JSON.stringify(body)
      });
      if (res.status === 401) {
        navigate("/dealer/login", { replace: true });
        return;
      }
      if (!res.ok) {
        const t = await res.text().catch(() => "");
        throw new Error(t || `HTTP ${res.status}`);
      }
      // success → go back to portal list
      navigate("/dealer/inventory");
    } catch (e) {
      setErr("Could not save changes.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-4">
      <DealerNav />

      <div className="flex items-center justify-between mb-4">
        <button
          onClick={() => navigate(-1)}
          className="px-3 py-2 text-sm rounded border border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
        >
          ← Back
        </button>
        <h1 className="text-xl font-semibold">Edit Vehicle #{id}</h1>
      </div>

      {err && (
        <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {err}
        </div>
      )}

      {loading || !car ? (
        <div className="p-6 text-gray-500">Loading…</div>
      ) : (
        <div className="bg-white rounded-xl border p-4">
          {/* Header summary */}
          <div className="flex items-start gap-4 mb-4">
            <img
              src={car.imageUrl}
              alt={`${car.year} ${car.make} ${car.model}`}
              className="w-28 h-20 object-cover rounded border"
            />
            <div>
              <div className="text-lg font-medium">
                {car.year} {car.make} {car.model}
              </div>
              <div className="text-sm text-gray-600">
                VIN: {car.vin || "—"} • DOM: {car.dom ?? "—"}d • Contacts: {car.contacts ?? 0}
              </div>
              {car.listingUrl && (
                <Link
                  to={car.listingUrl}
                  className="text-xs text-blue-600 underline"
                  target="_blank"
                >
                  Public listing
                </Link>
              )}
            </div>
          </div>

          {/* Form */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <label className="block">
              <span className="block text-sm text-gray-600 mb-1">Price</span>
              <input
                type="number"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                className="h-10 w-full px-3 rounded-lg border border-gray-300"
                placeholder="e.g. 18995"
                min="0"
              />
            </label>

            <label className="block">
              <span className="block text-sm text-gray-600 mb-1">Mileage</span>
              <input
                type="number"
                value={mileage}
                onChange={(e) => setMileage(e.target.value)}
                className="h-10 w-full px-3 rounded-lg border border-gray-300"
                placeholder="e.g. 55234"
                min="0"
              />
            </label>

            {/* <label className="block sm:col-span-2">
              <span className="block text-sm text-gray-600 mb-1">Trim</span>
              <input
                value={trim}
                onChange={(e) => setTrim(e.target.value)}
                className="h-10 w-full px-3 rounded-lg border border-gray-300"
                placeholder="e.g. XLE, LT, Sport"
              />
            </label> */}

            <label className="flex items-center gap-2 text-sm sm:col-span-2">
              <input
                type="checkbox"
                checked={sold}
                onChange={(e) => setSold(e.target.checked)}
              />
              Mark as sold
            </label>
          </div>

          <div className="mt-4 flex justify-end gap-2">
            <button
              onClick={() => navigate(-1)}
              className="h-10 px-4 rounded-lg border border-gray-300 bg-white hover:bg-gray-50"
              disabled={saving}
            >
              Cancel
            </button>
            <button
              onClick={save}
              disabled={saving}
              className="h-10 px-4 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
